import {
  ICareerSkillCluster,
  ICareerSkillEdge,
  ICareerSkillGraph,
  ICareerSkillNode,
} from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';

/**
 * Индекс карты навыков: колонки, узлы, связи (ТЗ 0070).
 *
 * 🔴 Ни одной строки геометрии здесь нет, и наоборот — в раскладке нет ни
 * одной строки про смысл данных. Разделение не косметическое: правило, которое
 * живёт внутри React-компонента, проверить нечем, и именно так первая сборка
 * прошлой спеки набрала 31 дефект при полностью зелёных гейтах.
 *
 * Индекс УСТОЙЧИВ к плохим данным, и это не перестраховка. Сервер выбрасывает
 * висячие рёбра, но между релизами фронт и бэк расходятся, а карта не имеет
 * права падать на узле с пропавшим кластером: экран без карты хуже карты с
 * одной странной колонкой.
 */

export const HIERARCHY = 'hierarchy';

/** Счёт группы: обязательные закрытые / обязательные всего и сколько дополнительных. */
export interface IClusterProgress {
  done: number;
  total: number;
  optional: number;
}

export interface ISkillGraphIndex {
  trackCode: string;
  trackTitle: string;
  trackIcon: string;
  /** Колонки в порядке показа. */
  clusters: ICareerSkillCluster[];
  byId: Map<string, ICareerSkillNode>;
  bySlug: Map<string, ICareerSkillNode>;
  /** Узлы колонки, в порядке показа. */
  nodesOf: Map<string, ICareerSkillNode[]>;
  /** Куда ведёт навык: id → [{id, kind}]. */
  opensOut: Map<string, ICareerSkillEdge[]>;
  /** Что нужно до навыка. */
  needsIn: Map<string, ICareerSkillEdge[]>;
  clusterOf: Map<string, string>;
  /**
   * Доля закрытых навыков в колонке, посчитанная по ПОЛНЫМ данным.
   *
   * 🔴 Считается здесь, а не на месте показа. Фильтры сужают `nodesOf`, и
   * счёт по нему после фильтра сообщал бы «0 из 3» там, где у человека
   * закрыто 5 из 12 — то есть кольцо прогресса профессии начинало бы отвечать
   * на вопрос про текущий вид, а не про профессию.
   */
  progressOf: Map<string, IClusterProgress>;
}

/**
 * Полный порядок узлов внутри колонки.
 *
 * 🔴 `localeCompare` здесь ЗАПРЕЩЁН. Он сортирует по таблицам ICU, а те
 * отличаются между сборками браузера и Node — и приёмочное требование ТЗ
 * «одинаковые данные дают одинаковые координаты» перестало бы выполняться
 * между машиной разработчика и телефоном студента. Сравниваем кодовые
 * единицы: `slug` — латиница по построению.
 */
const PRIORITY_RANK: Record<string, number> = { core: 0, important: 1, optional: 2 };

const bySlugAsc = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

export const compareNodes = (a: ICareerSkillNode, b: ICareerSkillNode): number =>
  a.clusterCode === b.clusterCode
    ? (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
      bySlugAsc(a.slug, b.slug)
    : bySlugAsc(a.clusterCode, b.clusterCode);

export const indexSkillGraph = (graph: ICareerSkillGraph | null): ISkillGraphIndex => {
  const empty: ISkillGraphIndex = {
    trackCode: '',
    trackTitle: '',
    trackIcon: '',
    clusters: [],
    byId: new Map(),
    bySlug: new Map(),
    nodesOf: new Map(),
    opensOut: new Map(),
    needsIn: new Map(),
    clusterOf: new Map(),
    progressOf: new Map(),
  };
  if (!graph) return empty;

  const byId = new Map<string, ICareerSkillNode>();
  const bySlug = new Map<string, ICareerSkillNode>();
  graph.nodes.forEach((node) => {
    // Дубль по id — данные сломаны; берём первый и не падаем.
    if (!byId.has(node.id)) byId.set(node.id, node);
    if (!bySlug.has(node.slug)) bySlug.set(node.slug, node);
  });

  // ⚠️ Порядок сервера НЕ принимается на веру. Смени бэкенд свой `ORDER BY` —
  // и узлы поехали бы на карте, хотя ни одна цифра не изменилась.
  const clusters = [...graph.clusters].sort(
    (a, b) => a.order - b.order || bySlugAsc(a.code, b.code),
  );
  const known = new Set(clusters.map((cluster) => cluster.code));

  const nodesOf = new Map<string, ICareerSkillNode[]>();
  const clusterOf = new Map<string, string>();
  const orphans: ICareerSkillNode[] = [];
  Array.from(byId.values())
    .sort(compareNodes)
    .forEach((node) => {
      if (!known.has(node.clusterCode)) {
        // Узел, чья колонка пропала. Не выбрасываем: навык, требуемый
        // профессией, обязан быть виден, даже если группу удалили.
        orphans.push(node);
        return;
      }
      clusterOf.set(node.id, node.clusterCode);
      const bucket = nodesOf.get(node.clusterCode);
      if (bucket) bucket.push(node);
      else nodesOf.set(node.clusterCode, [node]);
    });

  const shown = [...clusters];
  if (orphans.length) {
    shown.push({
      code: '__orphans__',
      title: '',
      description: '',
      icon: '',
      order: Number.MAX_SAFE_INTEGER,
    });
    nodesOf.set('__orphans__', orphans);
    orphans.forEach((node) => clusterOf.set(node.id, '__orphans__'));
  }

  const opensOut = new Map<string, ICareerSkillEdge[]>();
  const needsIn = new Map<string, ICareerSkillEdge[]>();
  graph.edges.forEach((edge) => {
    if (edge.kind === HIERARCHY) return;
    // Висячее ребро выбрасывает и сервер, но между релизами фронт и бэк
    // расходятся — на канвасе это была бы попытка нарисовать линию в никуда.
    if (!byId.has(edge.from) || !byId.has(edge.to)) return;
    const out = opensOut.get(edge.from);
    if (out) out.push(edge);
    else opensOut.set(edge.from, [edge]);
    const back = needsIn.get(edge.to);
    if (back) back.push(edge);
    else needsIn.set(edge.to, [edge]);
  });
  // Порядок связей тоже фиксируем: иначе клавиша «вправо» ведёт то к одному
  // навыку, то к другому.
  const order = (rows: ICareerSkillEdge[]) =>
    rows.sort(
      (a, b) =>
        (a.kind === 'required' ? 0 : 1) - (b.kind === 'required' ? 0 : 1) ||
        bySlugAsc(a.to, b.to) ||
        bySlugAsc(a.from, b.from),
    );
  opensOut.forEach(order);
  needsIn.forEach(order);

  // Доля закрытого считается ЗДЕСЬ, по полным данным, и дальше только читается.
  // 🔴 0072: как на сервере (`skill_progress.counted`) — дополнительные навыки
  // вне знаменателя. Раньше группа говорила «3 z 4» с optional внутри, а
  // главная и профессия — без, и одинаковые данные давали разные числа.
  const progressOf = new Map<string, IClusterProgress>();
  shown.forEach((cluster) => {
    const rows = nodesOf.get(cluster.code) || [];
    const required = rows.filter((row) => row.priority !== 'optional');
    progressOf.set(cluster.code, {
      done: required.filter(isClosed).length,
      total: required.length,
      optional: rows.length - required.length,
    });
  });

  return {
    trackCode: graph.trackCode,
    trackTitle: graph.trackTitle,
    trackIcon: graph.trackIcon,
    clusters: shown,
    byId,
    bySlug,
    nodesOf,
    opensOut,
    needsIn,
    clusterOf,
    progressOf,
  };
};

/** Окрестность узла на расстоянии одного шага — подсветка выбранного. */
export const neighbourhoodOf = (index: ISkillGraphIndex, nodeId: string | null): Set<string> => {
  const out = new Set<string>();
  if (!nodeId || !index.byId.has(nodeId)) return out;
  out.add(nodeId);
  (index.opensOut.get(nodeId) || []).forEach((edge) => out.add(edge.to));
  (index.needsIn.get(nodeId) || []).forEach((edge) => out.add(edge.from));
  return out;
};

/** Навык считается закрытым: подтверждён курсом или заявлен студентом. */
export const isClosed = (node: ICareerSkillNode): boolean =>
  node.status === 'confirmed' || node.status === 'declared';

/**
 * Сколько узлов колонки закрыто.
 *
 * Берёт готовый счёт из индекса, а не пересчитывает `nodesOf`: под фильтром
 * там остаются не все узлы, и дробь начала бы врать о профессии.
 */
export const clusterProgress = (index: ISkillGraphIndex, code: string): IClusterProgress =>
  index.progressOf.get(code) || { done: 0, total: 0, optional: 0 };

/**
 * «3 z 4» по обязательным навыкам и отдельно «+2 dodatkowe» (0072): ключ и
 * подстановки — одна функция на холст и список.
 */
export const clusterProgressText = (
  progress: IClusterProgress,
): { key: string; vars: Record<string, number> } =>
  progress.optional
    ? {
        key: 'careerCenter.map.clusterProgressOptional',
        vars: { done: progress.done, total: progress.total, optional: progress.optional },
      }
    : {
        key: 'careerCenter.map.clusterProgress',
        vars: { done: progress.done, total: progress.total },
      };
